import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Eye, Search, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import Menu from "../components/menu";
import DashboardShell from "../../components/DashboardShell";
import { useMenuState } from "../../Common/MenuStateContext";
import SaListPageShell from "../../components/sa/SaListPageShell";
import { FRAMEWORK_STUDIO_LABELS } from "../../utils/frameworkStudioLabels";
import ContentLoader from "../../components/ContentLoader";
import TableSortSelect from "../../components/TableSortSelect";
import { Button, Modal, Pagination } from "../../components/ui";
import { TABLE_SORT } from "../../utils/tableSort";
import { dashboardToast } from "../../utils/dashboardToast";
import {
  deleteTraxccelFramework,
  getTraxccelFramework,
  getTraxccelFrameworks,
} from "../../services/traxccelFrameworkApi";
import "../../assets/css/dashboard.css";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
};

/** Empty / missing cell display */
const EmptyCell = () => <span className="app-td__empty">—</span>;

type TreeQuestion = { id?: number; questionText?: string };

const renderQuestionStack = (questions: TreeQuestion[] | undefined) => {
  const items = (questions || []).filter((question) => question?.questionText?.trim());
  if (!items.length) {
    return <span className="rdm-empty">No questions</span>;
  }

  return (
    <div className="master-list-question-stack">
      {items.map((question, qIndex) => (
        <div key={question.id || qIndex} className="master-list-question-item">
          <span className="master-list-question-index">{qIndex + 1}.</span>
          <span>{question.questionText}</span>
        </div>
      ))}
    </div>
  );
};

const StateQuestions = ({
  state,
  questions,
}: {
  state: "current" | "future";
  questions: TreeQuestion[] | undefined;
}) => {
  const items = (questions || []).filter((q) => q?.questionText?.trim());
  const title = state === "current" ? "Current state" : "Future state";

  return (
    <div className="af-tree-state">
      <div className="af-tree-state__head">
        <span className="af-tree-state__label">{title}</span>
        <span className="af-tree-state__count">
          {items.length} question{items.length === 1 ? "" : "s"}
        </span>
      </div>
      {renderQuestionStack(questions)}
    </div>
  );
};

const questionCount = (q: TreeQuestion[] | undefined) =>
  (q || []).filter((x) => x?.questionText?.trim()).length;

const FrameworkCapabilities = ({ tree }) => {
  const rows = useMemo(
    () =>
      (Array.isArray(tree) ? tree : []).flatMap((fw) =>
        (fw.capabilities || []).map((cap) => {
          const subs = cap.subCapabilities || [];
          const total = subs.length
            ? subs.reduce(
                (n, s) =>
                  n + questionCount(s.currentStateQuestions) + questionCount(s.futureStateQuestions),
                0
              )
            : questionCount(cap.currentStateQuestions) + questionCount(cap.futureStateQuestions);
          return {
            key: `${fw.id}-${cap.id}`,
            source: fw.sourceFrameworkName || "Unassigned",
            cap,
            subs,
            total,
          };
        })
      ),
    [tree]
  );
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const allOpen = rows.length > 0 && rows.every((r) => expanded.has(r.key));
  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <section className="ab-view__card ab-view__main">
      <div className="ab-view__main-head tf-view__head">
        <h3>Capabilities</h3>
        {rows.length > 0 ? (
          <button
            type="button"
            className="ab-wizard__review-edit"
            onClick={() => setExpanded(allOpen ? new Set() : new Set(rows.map((r) => r.key)))}
          >
            {allOpen ? "Collapse all" : "Expand all"}
          </button>
        ) : null}
      </div>
      {rows.length === 0 ? (
        <p className="af-tree-empty tf-view__empty">No content in this assessment framework yet.</p>
      ) : (
        <>
          <div className="tf-view__row tf-view__row--head">
            <span />
            <span>Capability</span>
            <span>Source</span>
            <span className="tf-view__num">Subs</span>
            <span className="tf-view__num">Questions</span>
          </div>
          {rows.map((r) => {
            const open = expanded.has(r.key);
            return (
              <div key={r.key} className={open ? "tf-view__item tf-view__item--open" : "tf-view__item"}>
                <button
                  type="button"
                  className="tf-view__row"
                  aria-expanded={open}
                  onClick={() => toggle(r.key)}
                >
                  {open ? <ChevronDown size={16} aria-hidden /> : <ChevronRight size={16} aria-hidden />}
                  <span className="tf-view__name">{r.cap.capabilityName || "Unnamed capability"}</span>
                  <span className="tf-view__source">{r.source}</span>
                  <span className="tf-view__num">{r.subs.length || "–"}</span>
                  <span className="tf-view__num">{r.total}</span>
                </button>
                {open ? (
                  <div className="tf-view__body">
                    {r.cap.capabilityDescription?.trim() ? (
                      <p className="af-tree-capability__desc">{r.cap.capabilityDescription.trim()}</p>
                    ) : null}
                    {r.subs.length === 0 ? (
                      <div className="af-tree-questions-grid">
                        <StateQuestions state="current" questions={r.cap.currentStateQuestions} />
                        <StateQuestions state="future" questions={r.cap.futureStateQuestions} />
                      </div>
                    ) : (
                      r.subs.map((sub, i) => (
                        <div key={sub.subId || i} className="tf-view__sub">
                          <p className="ab-wizard__eyebrow">
                            {i + 1}. {sub.subCapabilityName || "Unnamed sub-capability"}
                          </p>
                          {sub.subCapabilityDescription?.trim() ? (
                            <p className="af-tree-sub__desc">{sub.subCapabilityDescription.trim()}</p>
                          ) : null}
                          <div className="af-tree-questions-grid">
                            <StateQuestions state="current" questions={sub.currentStateQuestions} />
                            <StateQuestions state="future" questions={sub.futureStateQuestions} />
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </>
      )}
    </section>
  );
};

const FrameworkView = ({ record, onDelete }) => {
  const stats = [
    { label: "frameworks", value: record.frameworkCount ?? 0 },
    { label: "capabilities", value: record.capabilityCount ?? 0 },
    { label: "sub-capabilities", value: record.subCapabilityCount ?? 0 },
    { label: "questions", value: record.questionCount ?? 0 },
  ];

  return (
    <div className="ab-view">
      <section className="ab-view__card ab-view__hero">
        <div className="ab-view__hero-top">
          <div className="ab-view__hero-main">
            <p className="ab-wizard__eyebrow">Assessment framework</p>
            <div className="ab-view__title-row">
              <h2 className="ab-view__title">{record.title}</h2>
              <span
                className={
                  record.isActive
                    ? "ab-view__status ab-view__status--published"
                    : "ab-view__status"
                }
              >
                {record.isActive ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="ab-view__meta">
              <span>Created {formatDate(record.createdAt)}</span>
            </div>
          </div>
          <div className="ab-view__actions">
            <Button variant="danger" onClick={onDelete}>
              <Trash2 size={16} aria-hidden />
              Delete
            </Button>
          </div>
        </div>
        <div className="ab-view__stats tf-view__stats">
          {stats.map((s) => (
            <div key={s.label}>
              <strong>{s.value}</strong> {s.label}
            </div>
          ))}
        </div>
      </section>

      <div className="ab-view__grid tf-view__grid">
        <FrameworkCapabilities tree={record.tree} />
        <aside className="ab-view__side">
          <section className="ab-view__card ab-view__panel">
            <h3>About this framework</h3>
            <p className="ab-wizard__eyebrow">Description</p>
            <p className="ab-view__about">{record.description?.trim() || "No description"}</p>
            <p className="ab-wizard__eyebrow">Status</p>
            <p className="ab-view__about">{record.isActive ? "Active" : "Inactive"}</p>
            <p className="ab-wizard__eyebrow">Created</p>
            <p className="ab-view__about">{formatDate(record.createdAt)}</p>
          </section>
        </aside>
      </div>
    </div>
  );
};

const TraxccelFrameworks = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const navigate = useNavigate();

  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState(TABLE_SORT.DATE_DESC);
  const [pageInfo, setPageInfo] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalCount: 0,
  });
  const [viewRecord, setViewRecord] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const viewFetchSeq = useRef(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleMenuSelection = useCallback((item) => {
    setSelectedItem(item);
    return item;
  }, []);

  const totalPages = Math.max(1, Math.ceil((pageInfo.totalCount || 0) / pageInfo.pageSize));

  const fetchList = useCallback(
    async (
      page = pageInfo.pageNumber,
      pageSize = pageInfo.pageSize,
      sort = sortKey,
      search = searchTerm
    ) => {
      setLoading(true);
      try {
        const result = await getTraxccelFrameworks(page, pageSize, sort, search);
        setItems(result.items || []);
        setPageInfo({
          pageNumber: result.pageNumber || page,
          pageSize: result.pageSize || pageSize,
          totalCount: result.totalCount || 0,
        });
      } catch (err) {
        dashboardToast.error(err.message || "Failed to load assessment frameworks");
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [pageInfo.pageNumber, pageInfo.pageSize, searchTerm, sortKey]
  );

  useEffect(() => {
    fetchList(1, pageInfo.pageSize, sortKey, searchTerm);
    // Initial load only; later changes go through handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = useCallback(
    (event) => {
      const value = event.target.value;
      setSearchTerm(value);
      fetchList(1, pageInfo.pageSize, sortKey, value);
    },
    [fetchList, pageInfo.pageSize, sortKey]
  );

  const handleSortChange = useCallback(
    (value) => {
      setSortKey(value);
      fetchList(1, pageInfo.pageSize, value, searchTerm);
    },
    [fetchList, pageInfo.pageSize, searchTerm]
  );

  const openView = useCallback(async (row) => {
    if (!row?.id) return;
    const fetchId = ++viewFetchSeq.current;
    setViewLoading(true);
    setViewRecord(null);
    try {
      const detail = await getTraxccelFramework(row.id);
      if (fetchId !== viewFetchSeq.current) return;
      setViewRecord(detail);
    } catch (err) {
      if (fetchId !== viewFetchSeq.current) return;
      dashboardToast.error(err?.message || "Failed to load details");
    } finally {
      if (fetchId === viewFetchSeq.current) {
        setViewLoading(false);
      }
    }
  }, []);

  const closeView = useCallback(() => {
    viewFetchSeq.current += 1;
    setViewLoading(false);
    setViewRecord(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      await deleteTraxccelFramework(deleteTarget.id);
      dashboardToast.success("Assessment framework deleted");
      setDeleteTarget(null);
      setViewRecord(null);
      await fetchList(pageInfo.pageNumber, pageInfo.pageSize, sortKey, searchTerm);
    } catch (err) {
      dashboardToast.error(err.message || "Failed to delete assessment framework");
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, fetchList, pageInfo.pageNumber, pageInfo.pageSize, searchTerm, sortKey]);

  return (
    <DashboardShell
      isMenuCollapsed={isMenuCollapsed}
      sidebar={
        <Menu
          onSelect={handleMenuSelection}
          selectedItem={selectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />
      }
    >
      <SaListPageShell topBarTitle={FRAMEWORK_STUDIO_LABELS.assessmentFrameworks}>
        <div className="dash-frame-parent1">
          <div className="dash-list-toolbar">
            <div className="dash-list-toolbar__intro">
              <h5 className="dash-list-toolbar__title">
                {FRAMEWORK_STUDIO_LABELS.assessmentFrameworks}
              </h5>
              <p className="dash-list-toolbar__desc">
                {FRAMEWORK_STUDIO_LABELS.assessmentFrameworksListDescription}
              </p>
            </div>
            <div className="dash-list-toolbar__actions">
              <div className="dash-search-field dash-table-search">
                <Search className="dash-search-field__icon" aria-hidden />
                <input
                  type="text"
                  placeholder="Search by title…"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="dash-search-field__input"
                  disabled={loading}
                />
              </div>
              <TableSortSelect variant="chip" value={sortKey} onChange={handleSortChange} disabled={loading} />
              <Button variant="primary" className="sa-create-btn" onClick={() => navigate("/sa/MasterList")}>
                {FRAMEWORK_STUDIO_LABELS.createAssessmentFrameworkAction}
              </Button>
            </div>
          </div>

          {loading ? (
            <ContentLoader label="Loading assessment frameworks…" />
          ) : (
            <Fragment>
              <div className="app-table-wrap app-table-wrap--refined">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Title</th>
                      <th>Description</th>
                      <th className="app-th--num">Frameworks</th>
                      <th className="app-th--num">Capabilities</th>
                      <th className="app-th--num">Subs</th>
                      <th className="app-th--num">Questions</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="text-center">
                          <span className="app-td__desc">
                            No assessment frameworks yet. Use Create Framework to select
                            capabilities and build one.
                          </span>
                        </td>
                      </tr>
                    ) : (
                      items.map((item, index) => {
                        const rowNumber =
                          (pageInfo.pageNumber - 1) * pageInfo.pageSize + index + 1;

                        return (
                          <tr
                            key={item.id}
                            className="rdm-row-clickable"
                            onClick={() => openView(item)}
                          >
                            <td>
                              <span className="app-td__index">{rowNumber}</span>
                            </td>
                            <td>
                              <span className="app-td__title">{item.title}</span>
                            </td>
                            <td>
                              {item.description ? (
                                <span className="app-td__desc" title={item.description}>
                                  {item.description}
                                </span>
                              ) : (
                                <EmptyCell />
                              )}
                            </td>
                            <td className="app-td--num">
                              <span className="app-td__num">{item.frameworkCount ?? 0}</span>
                            </td>
                            <td className="app-td--num">
                              <span className="app-td__num">{item.capabilityCount ?? 0}</span>
                            </td>
                            <td className="app-td--num">
                              <span className="app-td__num">{item.subCapabilityCount ?? 0}</span>
                            </td>
                            <td className="app-td--num">
                              <span className="app-td__num">{item.questionCount ?? 0}</span>
                            </td>
                            <td>
                              <span
                                className={
                                  item.isActive
                                    ? "app-status app-status--success"
                                    : "app-status app-status--muted"
                                }
                              >
                                {item.isActive ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td>
                              <span className="app-td__meta">{formatDate(item.createdAt)}</span>
                            </td>
                            <td
                              className="dash-mobile-table-actions"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <div className="btn-list-actions">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="btn-list-edit"
                                  title="View"
                                  aria-label={`View ${item.title}`}
                                  onClick={() => openView(item)}
                                >
                                  <Eye />
                                </Button>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  className="btn-list-delete"
                                  title="Delete"
                                  aria-label={`Delete ${item.title}`}
                                  onClick={() => setDeleteTarget(item)}
                                >
                                  <Trash2 />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                page={pageInfo.pageNumber}
                totalPages={totalPages}
                onPageChange={(page) => fetchList(page)}
                className="justify-center mt-4"
              />
            </Fragment>
          )}
        </div>
      </SaListPageShell>

      <Modal
        open={viewRecord != null || viewLoading}
        onClose={closeView}
        size="4xl"
        className="ab-view-modal"
        title="Assessment framework"
      >
        {viewLoading || !viewRecord ? (
          <ContentLoader label="Loading assessment framework details…" />
        ) : (
          <FrameworkView
            record={viewRecord}
            onDelete={() => {
              setDeleteTarget(viewRecord);
              closeView();
            }}
          />
        )}
      </Modal>

      <Modal
        open={deleteTarget != null}
        onClose={() => !deleting && setDeleteTarget(null)}
        title="Confirm Delete"
        footer={
          <>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
              isLoading={deleting}
              disabled={deleting}
            >
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </>
        }
      >
        <p className="m-0">
          Delete <strong>{deleteTarget?.title}</strong>? Capability catalog rows are not affected.
        </p>
      </Modal>
    </DashboardShell>
  );
};

export default TraxccelFrameworks;
